// NOTE: These are in a utils file so they can be tested without
//       having to worry about mocking Plotly (https://github.com/facebook/jest/issues/6420)

/**
 * This function does a few things for us:
 *
 * 1) It sorts the data from lowest median to highest
 * 2) Converts the data from the API into the format Plotly expects
 *    for building box plot traces
 */
export const buildBoxPlotTraces = data => {
  const boxPlotTraces = [];

  const sortedData = data?.sort(
    (a, b) => parseFloat(a.median) - parseFloat(b.median),
  );

  for (const item of sortedData) {
    const {
      disease,
      studyName,
      tissue,
      min,
      max,
      std,
      mean,
      median,
      percentile10,
      percentile25,
      percentile75,
      percentile90,
    } = item;

    const interQuantileRange = (percentile75 - percentile25) * 1.5;

    const dataPoint = {
      type: 'box',
      lowerfence: [Math.max(0, percentile25 - interQuantileRange)],
      mean: [mean],
      median: [median],
      name: studyName || tissue || disease,
      q1: [percentile25],
      q3: [percentile75],
      sd: [std],
      upperfence: [percentile75 + interQuantileRange],
      x: [studyName || tissue || disease],
      xaxis: 'x',
      yaxis: 'y',
      y: [[min, percentile10, percentile90, max]],
      showlegend: false,
    };
    boxPlotTraces.push(dataPoint);
  }

  return boxPlotTraces;
};

/**
 * We automatically add a horizontal trace across the box plot
 * for the gene expression level.  This takes the expression level
 * and creates a Plotly trace.
 */
export const buildHorizontalExpressionTrace = ({
  expressionValue,
  boxPlotTraces,
  traceName,
}) => {
  if (!boxPlotTraces) {
    return {};
  }

  // Note that the y-axis in the plot are in log2(tpm+1) unit and
  // the expression value from the PHC are in tpm.  We need to apply
  // the formula first to draw the line.
  const calculatedExpressionLevel = Math.log2(expressionValue + 1);

  const xTraces = boxPlotTraces?.flatMap(t => t.x);
  const yTraces = [...xTraces].fill(calculatedExpressionLevel);

  return {
    x: xTraces,
    y: yTraces,
    mode: 'lines',
    name: traceName,
    line: {
      width: 3,
    },
  };
};
